import clsx from 'clsx';
import { Coin } from '@hungryroot/icons';
import {
  Button,
  type ButtonProminenceVariant,
  type ButtonSizeVariant,
  type ButtonStyleVariant,
  Heading,
} from '@hungryroot/ramen';
import { MinusButton } from '../MinusButton/MinusButton';
import { PlusButton } from '../PlusButton/PlusButton';
import { TrashButton } from '../TrashButton/TrashButton';
import styles from './ManageCartButton.module.css';

export interface ManageCartButtonProps {
  numInCart: number;
  creditsForItem?: number;
  onSubtractClick: () => void;
  onAddClick: () => void;
  onRemoveClick: () => void;
  onIncrementClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  outOfStock?: boolean;
}

export function ManageCartButton(props: ManageCartButtonProps) {
  const {
    outOfStock,
    numInCart = 0,
    creditsForItem,
    onSubtractClick,
    onAddClick,
    onRemoveClick,
    onIncrementClick,
    isLoading,
    isDisabled,
  } = props;

  const noItemsInCart = numInCart === 0;

  const commonButtonProps = {
    style: 'default' as ButtonStyleVariant,
    prominence: 'primary' as ButtonProminenceVariant,
    size: 'medium' as ButtonSizeVariant,
    loading: isLoading,
    disabled: isDisabled,
  };

  if (outOfStock) {
    return (
      <Button
        {...commonButtonProps}
        disabled={true}
        buttonType="button"
        className={styles.button}
        centerContent
        onClick={onAddClick}
        testid="out-of-stock-button"
      >
        {!isLoading && <Heading variant="H6">Out of Stock</Heading>}
      </Button>
    );
  }

  if (noItemsInCart) {
    return (
      <Button
        {...commonButtonProps}
        buttonType="button"
        className={styles.button}
        centerContent
        onClick={onAddClick}
        testid="add-button"
      >
        {!isLoading && (
          <>
            <Heading variant="H6">Add to cart</Heading>
            <div className={styles['vertical-line']}></div>
            <div className={styles.credits}>
              <Coin />
              <Heading variant="H6">{creditsForItem}</Heading>
            </div>
          </>
        )}
      </Button>
    );
  }

  return (
    <Button
      {...commonButtonProps}
      buttonType="visual"
      className={clsx({ [styles['button-reset']]: !isDisabled })}
      centerContent={false}
    >
      {!isLoading && (
        <>
          {numInCart === 1 && (
            <TrashButton className={styles.trash} onClick={onRemoveClick} />
          )}
          {numInCart > 1 && (
            <MinusButton className={styles.minus} onClick={onSubtractClick} />
          )}
          <Heading variant="H6">{`${numInCart} in your cart`}</Heading>
          {numInCart > 0 && (
            <PlusButton className={styles.plus} onClick={onIncrementClick} />
          )}
        </>
      )}
    </Button>
  );
}
